import { useCallback, useEffect, useRef, useState } from 'react';

// Belangentafel-sessie: state + event-logging.
// Events worden fire-and-forget gelogd; een mislukt event mag het gesprek
// aan tafel nooit onderbreken (zichtbaar via console, niet via de UI).
export default function useSessie({ fetchWithAuth, buurtcode, addToast }) {
  const [actieveSessie, setActieveSessie] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [opnameActief, setOpnameActief] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const opnameStartRef = useRef(null);

  // Microfoon starten; mislukking blokkeert de sessie niet, maar wordt duidelijk gemeld.
  const startOpname = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.start(10000); // elke 10 s een chunk, zodat er niets verloren gaat
      recorderRef.current = recorder;
      opnameStartRef.current = Date.now();
      setOpnameActief(true);
      return true;
    } catch (e) {
      console.warn('Microfoon niet beschikbaar:', e);
      addToast && addToast('Geen microfoon — de sessie loopt zónder opname', 'warning');
      setOpnameActief(false);
      return false;
    }
  }, [addToast]);

  const stopOpnameEnUpload = useCallback(async (sessieId) => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    recorderRef.current = null;
    setOpnameActief(false);
    // Wacht tot de recorder zijn laatste chunk heeft afgegeven
    await new Promise(res => {
      recorder.onstop = res;
      recorder.stop();
    });
    recorder.stream.getTracks().forEach(t => t.stop());
    const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
    chunksRef.current = [];
    if (blob.size === 0) return;
    const duur = opnameStartRef.current ? (Date.now() - opnameStartRef.current) / 1000 : null;
    const form = new FormData();
    form.append('audio', blob, `sessie_${sessieId}.webm`);
    if (duur) form.append('duur_seconden', String(Math.round(duur)));
    // Startmoment van de opname meesturen: de microfoon start pas ná de
    // toestemmingsklik, dus later dan de sessiestart. De segmentatie legt
    // hiermee de audioklok en de event-tijdlijn exact op elkaar.
    if (opnameStartRef.current) {
      form.append('opname_gestart_op', new Date(opnameStartRef.current).toISOString());
    }
    try {
      const res = await fetchWithAuth(`/api/sessies/${sessieId}/opname`, { method: 'POST', body: form });
      const data = await res.json();
      if (data.success) {
        addToast && addToast('Opname geüpload — transcriptie is gestart', 'success');
      } else {
        addToast && addToast(data.error || 'Opname uploaden mislukt', 'error');
      }
    } catch (e) {
      addToast && addToast('Opname uploaden mislukt', 'error');
    }
  }, [fetchWithAuth, addToast]);

  // Bij het openen van de app: is er ergens nog een actieve sessie?
  // (bv. na een herlaadbeurt of als iemand vergat te stoppen)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth('/api/sessies');
        const data = await res.json();
        if (cancelled || !data.success) return;
        const actief = (data.sessies || []).find(s => s.status === 'actief');
        if (actief) setActieveSessie(actief);
      } catch (e) {
        console.warn('Sessies ophalen mislukt:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchWithAuth]);

  // Bij binnenlopen van een buurt: als dáár een actieve sessie loopt, die tonen.
  // Belangrijk: een lopende sessie wordt NOOIT uit beeld gegooid doordat de
  // gebruiker wegnavigeert — de opname loopt immers door.
  useEffect(() => {
    if (!buurtcode) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/sessies?buurtcode=${encodeURIComponent(buurtcode)}`);
        const data = await res.json();
        if (cancelled || !data.success) return;
        const actief = (data.sessies || []).find(s => s.status === 'actief');
        if (actief) setActieveSessie(actief);
      } catch (e) {
        console.warn('Sessies ophalen mislukt:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [buurtcode, fetchWithAuth]);

  const startSessie = useCallback(async (deelnemers) => {
    if (!buurtcode) return false;
    setBezig(true);
    try {
      const res = await fetchWithAuth('/api/sessies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buurtcode, toestemming: true, deelnemers }),
      });
      const data = await res.json();
      if (!data.success) {
        addToast && addToast(data.error || 'Sessie starten mislukt', 'error');
        return false;
      }
      setActieveSessie({ id: data.id, buurtcode, belangentafel_nr: data.belangentafel_nr, status: 'actief' });
      addToast && addToast(`Belangentafel ${data.belangentafel_nr} gestart`, 'success');
      await startOpname();
      return true;
    } catch (e) {
      addToast && addToast('Sessie starten mislukt', 'error');
      return false;
    } finally {
      setBezig(false);
    }
  }, [buurtcode, fetchWithAuth, addToast]);

  const stopSessie = useCallback(async () => {
    if (!actieveSessie) return;
    setBezig(true);
    try {
      // Eerst de opname veiligstellen, dan pas de sessie sluiten
      await stopOpnameEnUpload(actieveSessie.id);
      const res = await fetchWithAuth(`/api/sessies/${actieveSessie.id}/stop`, { method: 'POST' });
      const data = await res.json();
      if (!data.success) {
        addToast && addToast(data.error || 'Sessie stoppen mislukt', 'error');
        return;
      }
      setActieveSessie(null);
      addToast && addToast('Belangentafel gestopt — sessie staat klaar voor review', 'success');
    } catch (e) {
      addToast && addToast('Sessie stoppen mislukt', 'error');
    } finally {
      setBezig(false);
    }
  }, [actieveSessie, fetchWithAuth, addToast, stopOpnameEnUpload]);

  const logEvent = useCallback((eventType, projectnummer, lokatieNr, lokatieVersieId) => {
    if (!actieveSessie) return;
    fetchWithAuth(`/api/sessies/${actieveSessie.id}/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: eventType,
        projectnummer: projectnummer ?? null,
        lokatie_nr: lokatieNr ?? null,
        lokatie_versie_id: lokatieVersieId ?? null,
      }),
    }).catch(e => console.warn('Sessie-event loggen mislukt:', e));
  }, [actieveSessie, fetchWithAuth]);

  // Ref-variant zodat kaart-handlers (die buiten de React-render leven)
  // altijd bij de actuele logEvent kunnen zonder her-registratie.
  const logEventRef = useRef(logEvent);
  useEffect(() => { logEventRef.current = logEvent; }, [logEvent]);

  return { actieveSessie, bezig, opnameActief, startSessie, stopSessie, logEvent, logEventRef };
}
